import { useEffect, useState } from 'react';
import { api } from '../api/client';

interface Webhook {
  id: number;
  name: string;
  url: string;
  events: string[];
  is_active: boolean;
  last_triggered_at: string | null;
  last_status: number | null;
  created_at: string;
}

interface WebhookLog {
  id: number;
  event: string;
  status_code: number | null;
  error: string | null;
  created_at: string;
}

const EVENT_LABELS: Record<string, string> = {
  'agent.completed': 'Agent Completed',
  'agent.failed': 'Agent Failed',
  'project.created': 'Project Created',
  'project.updated': 'Project Updated',
  'knowledge.updated': 'Knowledge Updated',
  'alert.triggered': 'Alert Triggered',
  'share.created': 'Share Created',
  'integration.connected': 'Integration Connected',
  'integration.disconnected': 'Integration Disconnected',
};

export default function Webhooks() {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [viewLogs, setViewLogs] = useState<number | null>(null);
  const [logs, setLogs] = useState<WebhookLog[]>([]);

  useEffect(() => { fetchWebhooks(); }, []);

  const fetchWebhooks = async () => {
    try {
      const res = await api.get('/api/webhooks');
      setWebhooks(res.data.webhooks);
    } catch { setWebhooks([]); } finally { setLoading(false); }
  };

  const createWebhook = async () => {
    try {
      await api.post('/api/webhooks', { name: name || 'Untitled Webhook', url, events: selectedEvents });
      setShowCreate(false);
      setName(''); setUrl(''); setSelectedEvents([]);
      fetchWebhooks();
    } catch { /* silent */ }
  };

  const testWebhook = async (id: number) => {
    setTestingId(id);
    try {
      await api.post(`/api/webhooks/${id}/test`);
      fetchWebhooks();
    } catch { /* silent */ } finally { setTestingId(null); }
  };

  const fetchLogs = async (id: number) => {
    setViewLogs(id);
    try {
      const res = await api.get(`/api/webhooks/${id}/logs`);
      setLogs(res.data.logs);
    } catch { setLogs([]); }
  };

  const toggleEvent = (event: string) => {
    setSelectedEvents((prev) => prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event]);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Webhooks</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Receive real-time notifications via HTTP callbacks</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
          + New Webhook
        </button>
      </div>

      {showCreate && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 space-y-3">
          <p className="font-medium text-gray-800 dark:text-gray-100">Create Webhook</p>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Webhook name" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200" />
          <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Endpoint URL" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200" />
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Events</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(EVENT_LABELS).map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={selectedEvents.includes(key)} onChange={() => toggleEvent(key)} className="rounded" />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={createWebhook} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm">Create</button>
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm">Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="text-gray-500 dark:text-gray-400">Loading...</div></div>
      ) : webhooks.length === 0 ? (
        <div className="text-center py-12 text-gray-400 dark:text-gray-500">
          <p className="text-4xl mb-2">🔔</p>
          <p>No webhooks configured</p>
        </div>
      ) : (
        <div className="space-y-3">
          {webhooks.map((wh) => (
            <div key={wh.id} className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-gray-800 dark:text-gray-100">{wh.name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 break-all">{wh.url}</p>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${wh.is_active ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'}`}>
                  {wh.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {wh.events.map((ev) => (
                  <span key={ev} className="px-2 py-0.5 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-xs">{ev}</span>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between text-xs text-gray-400 dark:text-gray-500">
                <span>{wh.last_status ? `Last: ${wh.last_status}` : 'Never triggered'}</span>
                <span>Created {new Date(wh.created_at).toLocaleDateString()}</span>
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => testWebhook(wh.id)}
                  disabled={testingId === wh.id}
                  className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  {testingId === wh.id ? 'Testing...' : 'Test'}
                </button>
                <button onClick={() => fetchLogs(wh.id)} className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700">
                  Logs
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {viewLogs !== null && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="font-medium text-gray-800 dark:text-gray-100">Webhook Logs</p>
            <button onClick={() => setViewLogs(null)} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">Close</button>
          </div>
          {logs.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">No logs yet</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {logs.map((log) => (
                <div key={log.id} className="flex items-center justify-between text-sm py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                  <span className="text-gray-600 dark:text-gray-400">{log.event}</span>
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-0.5 rounded text-xs ${log.status_code && log.status_code >= 200 && log.status_code < 300 ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'}`}>
                      {log.status_code || 'ERR'}
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">{new Date(log.created_at).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
